import './index.scss';
import React from 'react';
import { Form, Button } from 'semantic-ui/semantic-ui-imports';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-semantic-toasts';
import Filter from 'Types/Filter';
import { AppSettingsContext } from 'contexts/appSettings';
import FilterInput from 'components/FormInputs/FilterInput';
import axios from '../../services/request';
import OneLineInputs from '../FormInputs/OneLineInputs';
import FilterSelect from '../FormInputs/FilterSelect';

interface Props {
  defaultValues?: { [key: string]: any };
  onSubmit: (params: { [key: string]: any }) => void;
}

/* 
  opConds.cooling.ambient.TIn = Sommer.Udeluft.Temperatur + 273.15 # Fra Celcius til Kelvin
  opConds.cooling.ambient.VIn = Luftmængder.Tilluft / 3600.0 # Fra m3/h til m3/s
  opConds.cooling.ambient.RHIn = Sommer.Udeluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
  opConds.cooling.indoor.TIn = Sommer.Fraluft.Temperatur + 273.15 # Fra Celcius til Kelvin
  opConds.cooling.indoor.VIn = Luftmængder.Fraluft / 3600.0 # Fra m3/h til m3/s
  opConds.cooling.indoor.RHIn = Sommer.Fraluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
  opConds.cooling.indoor.TOut = Ekspert.Indblæsningstemperatur_sommer.Ønsket_tillufftemperatur + 273.15
  opConds.cooling.indoor.calcTOut = Ekspert.Indblæsningstemperatur_sommer.[to be implemented, checkbox or similar method to toggle]

  opConds.heating.ambient.TIn = Vinter.Udeluft.Temperatur + 273.15 # Fra Celcius til Kelvin
  opConds.heating.ambient.VIn = Luftmængder.Tilluft / 3600.0 # Fra m3/h til m3/s
  opConds.heating.ambient.RHIn = Vinter.Udeluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
  opConds.heating.indoor.TIn = Vinter.Fraluft.Temperatur + 273.15 # Fra Celcius til Kelvin
  opConds.heating.indoor.VIn = Luftmængder.Fraluft / 3600.0 # Fra m3/h til m3/s
  opConds.heating.indoor.RHIn = Vinter.Fraluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
  opConds.heating.indoor.TOut = Ekspert.Indblæsningstemperatur_vinter.Ønsket_tillufftemperatur + 273.15
  opConds.heating.indoor.calcTOut = Ekspert.Indblæsningstemperatur_vinter.[to be implemented, checkbox or similar method to toggle]

*/

const ProductFilters = ({ defaultValues, onSubmit }: Props) => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = React.useState([]);
  const [params, setParams] = React.useState(defaultValues);
  const { getInfoText } = React.useContext(AppSettingsContext);

  const updateField = (key: string, value: any) => {
    setParams({ ...params, [key]: value });
  };

  const getFilterOptions = async () => {
    try {
      const { data } = await axios.get(`/filters/`);
      if (data) {
        const filters = data.sort((a: Filter, b: Filter) =>
          a.order < b.order ? -1 : 1
        );
        setFilterOptions(
          filters.map((item: { id: string; name: string }) => ({
            key: item.id,
            value: item.id,
            text: item.name,
          }))
        );
      }
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }
  };

  React.useEffect(() => {
    getFilterOptions();
  }, []);

  const yesNoOptions = [
    { key: 'true', value: true, text: t('common.yes') },
    { key: 'false', value: false, text: t('common.no') },
  ];

  return (
    <div className="product-filters">
      <Form size="mini">
        {
          // Luftmændger og ekstern tryktab
          // summer
        }
        <div className="product-filters__filter">
          <div className="product-filters__filter__title">
            {t('productFilters.airLevels.title')}
          </div>
          <OneLineInputs
            className="product-filters__filter__airflow-inputs"
            nameFirst="cooling.ambient.VIn"
            nameSecond="cooling.ambient.dp_external"
            label={t('productFilters.airLevels.enteringAir')}
            defaultFirstValue={
              defaultValues ? defaultValues['cooling.ambient.VIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['cooling.ambient.dp_external'] : ''
            }
            labelUnitFirst="㎥/h"
            labelUnitSecond="Pa"
            onChange={(name, value) => updateField(name, value)}
          />
          <OneLineInputs
            className="product-filters__filter__airflow-inputs"
            nameFirst="cooling.indoor.VIn"
            nameSecond="cooling.indoor.dp_external"
            label={t('productFilters.airLevels.exitingAir')}
            defaultFirstValue={
              defaultValues ? defaultValues['cooling.indoor.VIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['cooling.indoor.dp_external'] : ''
            }
            labelUnitFirst="㎥/h"
            labelUnitSecond="Pa"
            onChange={(name, value) => updateField(name, value)}
          />
        </div>
        <div className="product-filters__filter">
          {
            // grunddata luftstrømme
            // summer
          }
          <div className="product-filters__filter__title">
            {t('productFilters.basicDataTemperature.title')}
          </div>
          <div className="product-filters__filter__sub-title">
            {t('productFilters.basicDataTemperature.summer')}
          </div>
          <OneLineInputs
            nameFirst="cooling.ambient.TIn"
            nameSecond="cooling.ambient.RHIn"
            label={t(
              'productFilters.basicDataTemperature.summerAirflow.outdoorAir'
            )}
            defaultFirstValue={
              defaultValues ? defaultValues['cooling.ambient.TIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['cooling.ambient.RHIn'] : ''
            }
            onChange={(name, value) => updateField(name, value)}
          />
          <OneLineInputs
            nameFirst="cooling.indoor.TIn"
            nameSecond="cooling.indoor.RHIn"
            label={t(
              'productFilters.basicDataTemperature.summerAirflow.exitingAir'
            )}
            defaultFirstValue={
              defaultValues ? defaultValues['cooling.indoor.TIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['cooling.indoor.RHIn'] : ''
            }
            onChange={(name, value) => updateField(name, value)}
          />
          <FilterInput
            name="cooling.indoor.TOut"
            label={t(
              'productFilters.basicDataTemperature.summerAirflow.desiredFlow'
            )}
            unit="°C"
            defaultValue={
              defaultValues ? defaultValues['cooling.indoor.TOut'] : 26
            }
            onChange={(name, val) => updateField(name, parseInt(val, 10))}
            inputProps={{ min: 5, max: 30, style: { maxWidth: '2.857rem' } }}
            formFieldProps={{ style: { width: 'calc(100% - 2.14rem)' } }}
            disableInputName={'cooling.indoor.calcTOut'}
            disabledValue={params ? params['cooling.indoor.calcTOut'] : false}
            onDisabledChange={(name, val) => updateField(name, val)}
          />
          {
            // grunddata luftstrømme
            // vinter
          }

          <div className="product-filters__filter__sub-title">
            {t('productFilters.basicDataTemperature.winter')}
          </div>
          <OneLineInputs
            nameFirst="heating.ambient.TIn"
            nameSecond="heating.ambient.RHIn"
            label={t(
              'productFilters.basicDataTemperature.winterAirflow.outdoorAir'
            )}
            defaultFirstValue={
              defaultValues ? defaultValues['heating.ambient.TIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['heating.ambient.RHIn'] : ''
            }
            onChange={(name, value) => updateField(name, value)}
          />
          <OneLineInputs
            nameFirst="heating.indoor.TIn"
            nameSecond="heating.indoor.RHIn"
            label={t(
              'productFilters.basicDataTemperature.winterAirflow.exitingAir'
            )}
            defaultFirstValue={
              defaultValues ? defaultValues['heating.indoor.TIn'] : ''
            }
            defaultSecondValue={
              defaultValues ? defaultValues['heating.indoor.RHIn'] : ''
            }
            onChange={(name, value) => updateField(name, value)}
          />
          <FilterInput
            name="heating.indoor.TOut"
            label={t(
              'productFilters.basicDataTemperature.winterAirflow.desiredFlow'
            )}
            unit="°C"
            defaultValue={
              defaultValues ? defaultValues['heating.indoor.TOut'] : 26
            }
            onChange={(name, val) => updateField(name, parseInt(val, 10))}
            inputProps={{ min: 5, max: 30, style: { maxWidth: '2.857rem' } }}
            formFieldProps={{ style: { width: 'calc(100% - 2.14rem)' } }}
            disableInputName={'heating.indoor.calcTOut'}
            disabledValue={params ? params['heating.indoor.calcTOut'] : false}
            onDisabledChange={(name, val) => updateField(name, val)}
          />
        </div>
        <div className="product-filters__filter">
          <div className="product-filters__filter__title">
            {t('productFilters.functions.title')}
          </div>
          <FilterSelect
            name="active_cooling"
            label={t('productFilters.functions.activeCooling')}
            defaultValue={params ? params.active_cooling : true}
            onChange={(name, value) => updateField(name, value)}
            options={yesNoOptions}
          />
          {params?.active_cooling && (
            <FilterSelect
              name="var_speed_pack"
              label={t('productFilters.functions.variableCompressorSpeed')}
              defaultValue={params ? params.var_speed_pack : true}
              onChange={(name, value) => updateField(name, value)}
              options={yesNoOptions}
            />
          )}
        </div>
        <div className="product-filters__filter">
          <div className="product-filters__filter__title">
            {t('productFilters.filterClass.title')}
          </div>

          <FilterSelect
            name="supply_filter"
            label={t('productFilters.filterClass.outdoorAirFilter')}
            defaultValue={defaultValues ? defaultValues.supply_filter : 3}
            onChange={(name, value) => updateField(name, value)}
            options={filterOptions}
            helpText={getInfoText('productform.filters.entering.help_text')}
          />
          <FilterSelect
            name="discharge_filter"
            label={t('productFilters.filterClass.exitingAirFilter')}
            defaultValue={defaultValues ? defaultValues.discharge_filter : 1}
            onChange={(name, value) => updateField(name, value)}
            options={filterOptions}
            helpText={getInfoText('productform.filters.discharge.help_text')}
          />
          <FilterSelect
            name="dp_max_filter_type"
            label={t('productFilters.filterClass.pressureLossFilterType')}
            defaultValue={
              defaultValues ? defaultValues.dp_max_filter_type : 'EN13053'
            }
            onChange={(name, value) => updateField(name, value)}
            disabled={false}
            options={[
              {
                key: 'EN13053',
                value: 'EN13053',
                text: t('productFilters.filterClass.pressureLossEN13053_100Pa'),
              },
              {
                key: 'CUSTOM',
                value: 'CUSTOM',
                text: t('productFilters.filterClass.pressureLossCustom'),
              },
            ]}
          />
          {params?.dp_max_filter_type === 'CUSTOM' && (
            <FilterInput
              name="dp_max_filter"
              label={t('productFilters.filterClass.pressureLossFilter')}
              unit="Pa"
              defaultValue={
                defaultValues
                  ? parseInt(defaultValues.dp_max_filter, 10)
                  : undefined
              }
              disabledValue={
                params ? params.dp_max_filter_type !== 'EN13053' : false
              }
              onChange={(name, val) => updateField(name, parseInt(val, 10))}
            />
          )}
        </div>
      </Form>

      <div className="product-filters__calc-btn">
        <Button
          primary
          onClick={() => {
            onSubmit(params || {});
          }}
        >
          {t('productFilters.calculateData')}
        </Button>
      </div>
    </div>
  );
};

ProductFilters.defaultProps = {
  defaultValues: {},
};

export default ProductFilters;

/*
missing: 

opConds.cooling.indoor.TOut = Ekspert.Indblæsningstemperatur_sommer.Ønsket_tillufftemperatur + 273.15
opConds.cooling.indoor.calcTOut = Ekspert.Indblæsningstemperatur_sommer.[to be implemented, checkbox or similar method to toggle]

opConds.heating.indoor.TOut = Ekspert.Indblæsningstemperatur_vinter.Ønsket_tillufftemperatur + 273.15
opConds.heating.indoor.calcTOut = Ekspert.Indblæsningstemperatur_vinter.[to be implemented, checkbox or similar method to toggle]
*/
